import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faEye,
} from "@fortawesome/free-solid-svg-icons";

import Page from "../../common/Page";
import Card from "../../common/Card";
import Table from "../../common/Table";
import Loading from "../../common/Loading";
import Badge from "../../common/Badge";
import CircularProgress from "../../common/CircularProgress";
import APIManager from "../../../managers/APIManager";
import thumbnailPlaceholder from "../../../assets/images/thumbnail-placeholder.png";
import { useNavigate, useSearchParams } from "react-router";
import { capitalize, formatDate } from "../../../utils/manipulateText";
import Button from "../../common/Button";
import LanguageSelector from "../../common/LanguageSelector";

const AIVideoList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );

  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [isPublishedFilter, setIsPublishedFilter] = useState(() => {
    const isPublishedParam = searchParams.get("isPublished");
    if (isPublishedParam === "true") return true;
    if (isPublishedParam === "false") return false;
    return undefined;
  }); // undefined = all, true = published, false = unpublished

  const fetchVideos = useCallback(() => {
    if (!videoCreation || !siteVirgulId) {
      setLoading(false);
      navigate("/");
      return;
    }

    setLoading(true);
    APIManager.getAIVideos({
      siteVirgulId,
      page,
      perPage,
      isPublished: isPublishedFilter,
    })
      .then((res) => {
        setVideos(res?.data || []);
        setTotalItems(res?.totalItems || 0);
        setTotalPages(res?.totalPages || 1);
      })
      .catch((error) => {
        console.error("Error fetching AI videos:", error);
        setVideos([]);
        setTotalItems(0);
        setTotalPages(1);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [siteVirgulId, videoCreation, page, perPage, isPublishedFilter, navigate]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleFilterChange = (filterValue) => {
    setIsPublishedFilter(filterValue);
    setPage(1); // Reset to first page when filter changes

    // Update URL query parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (filterValue === undefined) {
      newSearchParams.delete("isPublished");
    } else {
      newSearchParams.set("isPublished", filterValue.toString());
    }
    navigate(`/ai-video/list?${newSearchParams.toString()}`, {
      replace: true,
      shallow: true,
    });
  };

  const nextPage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPage(Math.min(totalPages, page + 1));
  };

  const prevPage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPage(Math.max(1, page - 1));
  };

  const redirectToVideoDetail = (e, videoId) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/ai-video/${videoId}`);
  };

  const filterChips = [
    { value: undefined, label: t("show all"), key: "all" },
    { value: true, label: t("live videos"), key: "published" },
    { value: false, label: t("ready to publish videos"), key: "unpublished" },
  ];

  return (
    <Page
      title={
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <Button
              className="mr-4"
              variant="secondary"
              icon={faArrowLeft}
              onClick={() => navigate(-1)}
            >
              {t("back")}
            </Button>

            <h1 className="text-3xl font-bold text-gray-900">
              {t("ai videos")}
            </h1>
          </div>
          <LanguageSelector />
        </div>
      }
    >
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm font-semibold text-gray-600">
            {capitalize(t("filter"))}:
          </label>
          {filterChips.map((chip) => (
            <button
              key={chip.key}
              onClick={() => handleFilterChange(chip.value)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                isPublishedFilter === chip.value
                  ? "bg-empower-500 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <Card>
          <div className="overflow-x-scroll">
            <Table
              headers={[
                { content: "thumbnail", className: "text-left" },
                { content: "id", className: "text-left" },
                { content: "template", className: "text-left" },
                { content: "creation type", className: "text-left" },
                { content: "progress", className: "text-center" },
                { content: "is published", className: "text-center" },
                { content: "updated at", className: "text-left" },
                { content: "actions", className: "text-center" },
              ]}
            >
              {videos?.map((video) => (
                <Table.Row
                  key={`video-${video.id}`}
                  dataKey={video.id}
                  className="even:bg-gray-100 hover:bg-gray-200 cursor-pointer"
                  onClick={(e) => redirectToVideoDetail(e, video.id)}
                >
                  <Table.Col>
                    <img
                      src={video.thumbnail || thumbnailPlaceholder}
                      alt={video.id || t("video thumbnail")}
                      className="w-20 h-12 object-cover rounded"
                      onError={(e) => {
                        e.target.src = thumbnailPlaceholder;
                      }}
                    />
                  </Table.Col>
                  <Table.Col className="text-left">
                    <div className="font-mono text-sm">{video.id}</div>
                  </Table.Col>
                  <Table.Col className="text-left">
                    <div className="text-sm">{video.template || "-"}</div>
                  </Table.Col>
                  <Table.Col className="text-left">
                    <div className="text-sm italic">
                      {video.creationType || "-"}
                    </div>
                  </Table.Col>
                  <Table.Col className="text-center">
                    <div className="flex items-center justify-center">
                      <CircularProgress
                        percentage={Number(video.progress || 0) * 100}
                        size={40}
                        strokeWidth={4}
                        color="text-empower-500"
                        backgroundColor="text-gray-200"
                        fontSize={10}
                      />
                    </div>
                  </Table.Col>
                  <Table.Col className="text-center">
                    {video.isPublished === 1 || video.isPublished === true ? (
                      <Badge color="success">{t("ai video published")}</Badge>
                    ) : (
                      <Badge color="secondary">
                        {t("ai video unpublished")}
                      </Badge>
                    )}
                  </Table.Col>
                  <Table.Col className="text-left">
                    <div className="text-sm">
                      {video.updatedAt ? formatDate(video.updatedAt) : "-"}
                    </div>
                  </Table.Col>
                  <Table.Col className="text-center">
                    <Button
                      variant="secondary"
                      className="w-full"
                      icon={faEye}
                      onClick={() => navigate(`/ai-video/${video.id}`)}
                    >
                      {t("view details")}
                    </Button>
                  </Table.Col>
                </Table.Row>
              ))}
            </Table>
          </div>

          {videos.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              {t("no videos found")}
            </div>
          )}

          {videos.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex flex-1 row gap-2 items-center text-sm">
                  <p className="text-nowrap">
                    {capitalize(t("records per page"))}
                  </p>
                  <select
                    className="block p-1 bg-gray-200 text-gray-700 border border-gray-200 rounded p-0 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    value={perPage}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                <div>
                  <p className="text-sm leading-5 text-gray-700">
                    {t("showing")}{" "}
                    <span className="font-medium">
                      {totalItems === 0 ? 0 : (page - 1) * perPage + 1}
                    </span>{" "}
                    {t("to")}{" "}
                    <span className="font-medium">
                      {Math.min(page * perPage, totalItems)}
                    </span>{" "}
                    {t("of")} <span className="font-medium">{totalItems}</span>{" "}
                    {t("videos")}
                  </p>
                </div>
                <div className="flex flex-1">
                  <nav className="relative z-0 inline-flex shadow-sm ml-auto">
                    {page === 1 ? (
                      <button
                        type="button"
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-400 transition ease-in-out duration-150 cursor-not-allowed"
                        aria-label="Previous"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-600 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-600 transition ease-in-out duration-150"
                        aria-label="Previous"
                        onClick={prevPage}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                      </button>
                    )}
                    {page >= totalPages ? (
                      <button
                        type="button"
                        className="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-400 transition ease-in-out duration-150 cursor-not-allowed"
                        aria-label="Next"
                      >
                        <FontAwesomeIcon icon={faChevronRight} size="sm" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-600 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-600 transition ease-in-out duration-150"
                        aria-label="Next"
                        onClick={nextPage}
                      >
                        <FontAwesomeIcon icon={faChevronRight} size="sm" />
                      </button>
                    )}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}
    </Page>
  );
};

export default AIVideoList;
